<script setup lang="ts">
import { absRatio, formatRatioPercent, negateMoney } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import KpiChangeIndicator from '@/features/dashboard/components/KpiChangeIndicator.vue'
import { visibleCategoryRows } from '../internal/category-rows.ts'
import { changeBetween } from '../internal/kpi-change.ts'
import type { CategorySpending } from '../internal/types.ts'

const props = defineProps<{
  spending: CategorySpending
  nameOf: (accountId: string) => string | undefined
  windowDays: number
}>()

const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

const rows = computed(() => visibleCategoryRows(props.spending.items, props.spending.total.currency))
const change = computed(() =>
  changeBetween(props.spending.previousTotal, props.spending.total, props.windowDays),
)
</script>

<template>
  <div class="flex flex-col gap-[var(--space-group)]">
    <div class="flex flex-wrap items-end justify-between gap-[var(--space-inline)]">
      <div class="flex flex-col gap-1">
        <MoneyAmount :value="negateMoney(spending.total)" sign size="large" tone="expense" />
        <KpiChangeIndicator
          :change="change"
          favourable="down"
          :caption="t('dashboard.vsSamePointLastMonth')"
        />
      </div>
    </div>
    <p v-if="rows.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('dashboard.noSpending') }}
    </p>
    <ol v-else class="flex flex-col">
      <li
        v-for="item in rows"
        :key="item.accountId ?? 'other'"
        class="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-[var(--space-inline)] border-t border-[color:var(--color-border)] py-[var(--space-inline)] first:border-t-0"
      >
        <span class="truncate text-[color:var(--color-text)]">{{
          item.accountId === undefined ? t('dashboard.otherCategory') : (nameOf(item.accountId) ?? '')
        }}</span>
        <span class="whitespace-nowrap font-semibold tabular-nums text-[color:var(--color-expense)]">
          <MoneyAmount :value="negateMoney(item.amount)" sign />
        </span>
        <span class="w-12 text-right tabular-nums text-[color:var(--color-text-muted)]">
          <template v-if="item.share !== undefined">
            {{ formatRatioPercent(absRatio(item.share), { locale: localeCode }) }}%
          </template>
        </span>
      </li>
    </ol>
  </div>
</template>
